import { Clip, FormFieldType } from '@air/api/types';
import { uploadArraySelector } from '@air/redux-uploader';
import { createSelector } from 'reselect';

import { getClipFormFieldValue } from '~/components/BoardCollection/utils';
import { AirState } from '~/store/types';

const baseBoardCollectionSelector = ({ boardCollection }: AirState) => boardCollection;

export const boardCollectionSessionIdSelector = createSelector(
  baseBoardCollectionSelector,
  ({ sessionId }) => sessionId,
);

export const boardCollectionShortIdSelector = createSelector(baseBoardCollectionSelector, ({ shortId }) => {
  if (!shortId) {
    throw new Error('shortId is not defined!');
  }

  return shortId;
});

export const boardCollectionAttemptedSubmissionFormSelector = createSelector(
  baseBoardCollectionSelector,
  ({ attemptedSubmission }) => attemptedSubmission,
);

export const boardCollectionHasSubmittedFormSelector = createSelector(
  baseBoardCollectionSelector,
  ({ hasSubmitted }) => hasSubmitted,
);

export const boardCollectionClipsSelector = createSelector(baseBoardCollectionSelector, ({ clips }) => clips);

export const boardCollectionHasClipsSelector = createSelector(
  [boardCollectionClipsSelector, uploadArraySelector],
  (clips, uploads) => !!Object.keys(clips).length || !!uploads.length,
);

export const boardCollectionFormSelector = createSelector(baseBoardCollectionSelector, ({ form }) => {
  if (!form) {
    throw new Error('form is not defined!');
  }

  return form;
});

export const boardCollectionFieldsSelector = createSelector(boardCollectionFormSelector, (form) => {
  return form?.fields || [];
});

const boardCollectionRequiredFieldsSelector = createSelector(
  boardCollectionFieldsSelector,
  (fields) => fields.filter(({ required }) => required) || [],
);

export const boardCollectionHasCustomFieldsSelector = createSelector(boardCollectionFormSelector, (form) => {
  if (!form || !form.fields.length) return false;

  return form.fields.some((field) => field.type === FormFieldType.customField);
});

export const boardCollectionInvalidFieldsSelector = createSelector(
  [boardCollectionRequiredFieldsSelector, boardCollectionClipsSelector],
  (requiredFields, clips) =>
    requiredFields.filter((field) => Object.values(clips).find((clip) => !getClipFormFieldValue(clip, field))),
);

export const boardCollectionClipsInvalidFieldsSelector = createSelector(
  [boardCollectionInvalidFieldsSelector, boardCollectionClipsSelector, (_st: AirState, ids: Clip['id'][]) => ids],
  (invalidFields, clips, clipIds) =>
    invalidFields.filter((field) =>
      Object.values(clips)
        .filter((clip) => clipIds.includes(clip.id))
        .find((clip) => !getClipFormFieldValue(clip, field)),
    ),
);

export const boardCollectionClipsInvalidFieldsLengthSelector = createSelector(
  [boardCollectionClipsInvalidFieldsSelector],
  (fields) => fields.length,
);

export const boardCollectionFormIsValidSelector = createSelector(
  [boardCollectionInvalidFieldsSelector],
  (fields) => !fields.length,
);

export const boardCollectionFormIsInvalidSelector = createSelector(
  [boardCollectionAttemptedSubmissionFormSelector, boardCollectionClipsInvalidFieldsLengthSelector],
  (attemptedSubmission, invalidFields) => attemptedSubmission && invalidFields > 0,
);

export const boardCollectionViewSelector = createSelector(baseBoardCollectionSelector, ({ view }) => view);
